import { ReactNode, useEffect } from 'react';
import { useAltHotkeys } from '../../../../hooks/useAltHotkeys';
import { useModalDrag } from '../../../../hooks/useModalDrag';
import ModalHeader from '../../../../components/ui/ModalHeader';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
}

export function Modal({ open, onClose, title, children, footer }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Alt+C closes, alongside the Escape handler above. Shell: the consuming modal
  // owns the footer, claims the layer after us, and wins.
  useAltHotkeys({ C: onClose }, undefined, { layer: open });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(open);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 bg-scrim/60 backdrop-blur-sm z-40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg w-full max-w-lg shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader title={title} onClose={onClose} dragHandleProps={dragHandleProps} />
        <div className="px-5 py-4 max-h-[60vh] overflow-y-auto text-slate-900 dark:text-slate-100">{children}</div>
        {footer && (
          <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-2">{footer}</div>
        )}
      </div>
    </div>
  );
}
